import React, { useState, useEffect } from 'react'
import { ArrowRight, Calendar, Clock, User } from 'lucide-react'

const RelatedPosts = ({ currentPost, onPostSelect }) => {
  const [relatedPosts, setRelatedPosts] = useState([])

  useEffect(() => {
    // Load all news items from localStorage
    const savedNews = localStorage.getItem('shikdar_films_news')
    if (savedNews) {
      const allNews = JSON.parse(savedNews)
      
      // Filter blog posts only (exclude news items)
      const blogPosts = allNews.filter(item => item.type === 'blog' && item.id !== currentPost.id)
      
      // Find posts in same category
      const sameCategoryPosts = blogPosts.filter(post => post.category === currentPost.category)
      
      // Find posts by same author
      const sameAuthorPosts = blogPosts.filter(post => post.author === currentPost.author && post.category !== currentPost.category)
      
      // Combine and limit to 3 posts
      let related = [...sameCategoryPosts, ...sameAuthorPosts]
      
      // If not enough related posts, add other recent posts
      if (related.length < 3) {
        const otherPosts = blogPosts.filter(post => 
          !related.some(r => r.id === post.id)
        )
        related = [...related, ...otherPosts]
      }
      
      // Limit to 3 posts and shuffle for variety
      setRelatedPosts(related.slice(0, 3))
    }
  }, [currentPost])

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' }
    return new Date(dateString).toLocaleDateString('en-US', options)
  }

  const handleImageError = (e) => {
    e.target.src = 'https://images.pexels.com/photos/7991579/pexels-photo-7991579.jpeg?auto=compress&cs=tinysrgb&w=800'
  }

  const getAuthorName = (authorId) => {
    const authors = {
      'kamal-hossain': 'Kamal Hossain',
      'tanvir-ahmed': 'Tanvir Ahmed',
      'sadia-rahman': 'Sadia Rahman'
    }
    return authors[authorId] || 'Unknown Author'
  }

  if (relatedPosts.length === 0) {
    return null
  }

  return (
    <div className="mt-16 pt-12 border-t border-gray-200">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-1 h-8 bg-bengal-green rounded-full"></div>
        <h3 className="text-3xl font-bold text-gray-900 section-title">Related Articles</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {relatedPosts.map((post) => (
          <article 
            key={post.id}
            onClick={() => onPostSelect(post)}
            className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer border border-gray-100 hover:border-bengal-green"
          >
            <div className="relative h-48 overflow-hidden">
              <img 
                src={post.image}
                alt={post.title}
                onError={handleImageError}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute top-4 left-4">
                <span className="inline-block px-3 py-1 bg-bengal-green text-white rounded-full text-xs font-semibold capitalize">
                  {post.category.replace('-', ' ')}
                </span>
              </div>
            </div>

            <div className="p-6">
              <h4 className="text-xl font-semibold text-gray-900 mb-3 section-title line-clamp-2 group-hover:text-bengal-green transition-colors">
                {post.title}
              </h4>

              <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                {post.excerpt}
              </p>

              <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(post.date)}</span>
                </div>
                {post.readTime && (
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{post.readTime}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <User className="w-4 h-4" />
                  <span>{getAuthorName(post.author)}</span>
                </div>
                <button className="inline-flex items-center gap-2 text-bengal-green hover:text-bengal-orange font-medium text-sm transition-colors duration-200">
                  Read More
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  )
}

export default RelatedPosts